import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useSiteMasterSession } from '@/hooks/useSiteMasterSession';
import { useMiniprogramAnalytics } from '@/hooks/useMiniprogramAnalytics';
import {
  MiniprogramAnalyticsMobilePanel,
  type AnalyticsMpOption,
} from '@/components/miniprogramAnalytics/MiniprogramAnalyticsMobilePanel';
import { siteMasterPaths } from '@/lib/siteMasterPaths';

export function SiteMasterAnalyticsPage() {
  const { mpId: mpIdParam } = useParams();
  const navigate = useNavigate();
  const { hasMpAnalytics } = useSiteMasterSession();
  const routeMpId = Math.max(0, parseInt(mpIdParam || '0', 10) || 0);

  const [days, setDays] = useState(14);
  const [mpList, setMpList] = useState<AnalyticsMpOption[]>([]);
  const [mpLoading, setMpLoading] = useState(true);

  const mpId = routeMpId > 0 ? routeMpId : mpList[0]?.id ?? 0;
  const { data, loading, refreshing, err, load } = useMiniprogramAnalytics(mpId, days, 60000, 'site_master_get_miniprogram_analytics');

  const loadMpList = useCallback(async () => {
    setMpLoading(true);
    try {
      const r = await apiFetch<{ ok: boolean; items: AnalyticsMpOption[] }>('site_master_list_analytics_miniprograms', {
        method: 'GET',
      });
      setMpList(r.ok ? r.items : []);
    } catch {
      setMpList([]);
    } finally {
      setMpLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!hasMpAnalytics) return;
    void loadMpList();
  }, [hasMpAnalytics, loadMpList]);

  useEffect(() => {
    if (mpLoading || routeMpId > 0 || mpList.length === 0) return;
    navigate(siteMasterPaths.analyticsFor(mpList[0].id), { replace: true });
  }, [mpLoading, routeMpId, mpList, navigate]);

  if (!hasMpAnalytics) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-bold text-slate-900">访问统计</h1>
        <div className="rounded-3xl border border-amber-200 bg-amber-50 px-5 py-4 text-base leading-relaxed text-amber-950">
          您尚未开通「小程序分析员」权限，或管理员尚未为您配置可查看的小程序。请联系系统管理员在「站长管理」中开通并绑定小程序。
        </div>
        <Link to={siteMasterPaths.home} className="inline-flex text-base font-semibold text-teal-700 underline">
          返回首页
        </Link>
      </div>
    );
  }

  return (
    <MiniprogramAnalyticsMobilePanel
      mpList={mpList}
      mpLoading={mpLoading}
      mpEmptyHint="管理员尚未为您绑定可查看的小程序，请联系系统管理员。"
      mpId={mpId}
      onSelectMp={(id) => navigate(siteMasterPaths.analyticsFor(id))}
      days={days}
      onDaysChange={setDays}
      data={data}
      loading={loading}
      refreshing={refreshing}
      err={err}
      onRefresh={() => void load(true)}
      footerNote="各小程序独立统计；绑定主小程序时从属小程序数据一并开放查看。"
    />
  );
}
